import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORT_BODY_MAX } from "../../utils/constants/support";
import { guardUnsavedWork } from "../../utils/helpers/unsavedWork";
import { AttachButton, AttachmentChips } from "./AttachmentPicker";

/**
 * Reply box for a thread: text + attachments + send.
 *
 * `onSend({ body, files })` must return a promise and REJECT on failure — the
 * draft is cleared only once the send succeeds, so a failed upload never eats
 * what was typed. The caller reports the error. Ctrl/Cmd+Enter sends.
 */
export default function Composer({ onSend, placeholder, disabled = false }) {
  const { t } = useTranslation();
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const dirty = body.trim().length > 0 || files.length > 0;
  useEffect(() => guardUnsavedWork(dirty), [dirty]);

  const canSend = dirty && !sending && !disabled;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await onSend({ body: body.trim(), files });
      setBody("");
      setFiles([]);
    } catch (_e) {
      /* kept the draft; the caller already said what went wrong */
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      send();
    }
  };

  return (
    <div className="rounded-2xl border border-[color:var(--support-border)] bg-[color:var(--support-surface)] p-3 space-y-2">
      <AttachmentChips
        files={files}
        disabled={sending}
        onRemove={(index) => setFiles((current) => current.filter((_, i) => i !== index))}
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={3}
        maxLength={SUPPORT_BODY_MAX}
        placeholder={placeholder}
        disabled={sending || disabled}
        aria-label={placeholder}
        // border-0/p-0/focus:ring-0 undo @tailwindcss/forms, which boxes every
        // textarea; the composer card is the frame here.
        className="w-full resize-y min-h-[72px] border-0 p-0 bg-transparent outline-none focus:ring-0 text-sm leading-relaxed text-[color:var(--support-surface-text)] placeholder:text-[color:var(--support-muted)] disabled:opacity-60"
      />
      <div className="flex items-center justify-between gap-2">
        <AttachButton files={files} onChange={setFiles} disabled={sending || disabled} />
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline text-[11px] text-[color:var(--support-muted)]">
            {t("support.thread.sendHint")}
          </span>
          <button
            type="button"
            onClick={send}
            disabled={!canSend}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold bg-[color:var(--support-mine-bg)] text-[color:var(--support-mine-text)] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2"
          >
            {sending ? t("support.sending") : t("support.thread.send")}
          </button>
        </div>
      </div>
    </div>
  );
}
